import { useState, useEffect, useCallback, useMemo } from "react";
import { notification, Button, Space } from "antd";
import { FormOutlined, BookOutlined, SwapOutlined } from "@ant-design/icons";
import "./KiboxManufactureList.scss";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import DeleteButton from "../DeleteButton/DeleteButton.jsx";
import withNavigation from '../hooks/withNavigation.jsx';
import SpareModal from "../SpareModal/index.js";
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { formatDate, compareDates } from '../../utils/dates';
import { useGetModelsLookupQuery, useGetVersionsLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function KiboxManufactureList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
    showModal: false,
  });

  const [data, setData] = useState({
    raw: [],
    selected: null,
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();

  // ===== 3. DERIVADOS (Computados) =====
  const selectedId = data.selected?.ki_id || "";

  const kiboxModels = models.filter(model => 
    ['kibox', 'kibox_legacy'].includes(model.device_type)
  ) || [];

  const modelFilter = kiboxModels.map(model => ({
    text: model.name,
    value: model.uuid
  }));

  const versionFilter = kiboxModels
    .map(model => ({
      text: model.name,
      value: model.uuid,
      children: versions
        .filter(v => v.model_uuid === model.uuid)
        .map(v => ({ text: v.name, value: v.uuid }))
    }))
    .filter(group => group.children.length > 0);

  // ===== 4. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    try {
      const res = await service.get("boards/manufacture/Fabricado");

      if (!res || res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res?.data?.message || "Error al obtener datos"),
        });
        updateData('raw', []);
        return;
      }

      updateData('raw', res.data);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [lang]);

  // Enriquece los datos con nombres de versión y modelo
  const enrichedData = useMemo(() => {
    return data.raw.map((board) => {
      const version = versions.find(v => v.uuid === board.version_uuid);
      let version_name = '-';
      let model_name = '-';
      let model_uuid = '';

      if (board.version_uuid && version) {
        version_name = version.name;
        model_uuid = version.model_uuid;
        const model = models.find(m => m.uuid === version.model_uuid);
        if (model) {
          model_name = model.name;
        }
      }

      const dateFormated = formatDate(board.date);

      return {
        ...board,
        version_name,
        model_name,
        model_uuid,
        dateFormated
      };
    });
  }, [data.raw, versions, models]);

  // ===== 6. HANDLERS =====
  const handleOpenSpareModal = () => updateUI('showModal', true);
  const handleCloseSpareModal = () => updateUI('showModal', false);

  const deletePlaca = async () => {
    updateUI('loading', true);
    try {
      const res = await service.del(`boards/${selectedId}`, selectedId);
      if (res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res.data.message),
        });
      } else {
        notification["success"]({
          message: lang.handle('Placa borrada correctamente'),
        });
      }
      await Pfetch();
      updateData('selected', null);
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  };

  const updatePlaca = () => {
    updateUI('loading', true);
    navigate(`/boards/manufacture/edit/1/${selectedId}`);
  };

  const assignPlaca = () => {
    updateUI('loading', true);
    navigate(`/boards/manufacture/assign/${selectedId}`);
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    Pfetch().then(() => {
      updateUI('loading', false);
    });
  }, [Pfetch]);

  // ===== 8. CONFIG (columnas, etc) =====
  const onSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selected', selectedRows.length > 0 ? selectedRows[0] : null);
  };

  const columns = [
    {
      title: 'Id',
      dataIndex: "ki_id",
      ...GetColumnLiteralSearchProps("ki_id", lang),
    },
    {
      title: "UUID",
      dataIndex: "uuid",
      ...GetColumnLiteralSearchProps("uuid", lang),
    },
    {
      title: lang.handle('Version hardware'),
      dataIndex: "hardware_version",
    },
    {
      title: "MAC / IMEI",
      dataIndex: "physical_id",
      ...GetColumnLiteralSearchProps("physical_id", lang),
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "dateFormated",
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: lang.handle('Modelo'),
      dataIndex: "model_name",
      filters: modelFilter,
      onFilter: (value, record) => record.model_uuid === value,
      sorter: (a, b) => (a.model_name || '').localeCompare(b.model_name || '')
    },
    {
      title: lang.handle('Versión'),
      dataIndex: "version_name",
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
      sorter: (a, b) => (a.version_name || '').localeCompare(b.version_name || ''),
    },
    {
      title: lang.handle('Notas'),
      dataIndex: "notes",
    },
  ];

  let selection = null;
  if (hasPrivilege('manufacture_delete') || hasPrivilege('manufacture_edit') || hasPrivilege('assign')) {
    selection = {
      type: 'radio',
      selectedRowKeys: data.selected?.ki_id ? [data.selected.ki_id] : [],
      onChange: onSelectionChange,
    };
  }

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        columns={columns}
        dataSource={enrichedData}
        rowKey="ki_id"
        rowSelection={selection}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('manufacture_delete') && (
            <DeleteButton
              onEvent={deletePlaca}
              buttonDisabled={!data.selected}
            />
          )}

          {hasPrivilege('manufacture_edit') && (
            <Button
              type="primary"
              icon={<FormOutlined />}
              disabled={!data.selected}
              onClick={updatePlaca}
            >
              {lang.handle('Editar')}
            </Button>
          )}

          {hasPrivilege('assign') && (
            <Button
              type="primary"
              icon={<BookOutlined />}
              disabled={!data.selected}
              onClick={assignPlaca}
            >
              {lang.handle('Asignar')}
            </Button>
          )}

          {hasPrivilege('assign') && (
            <Button
              type="primary"
              icon={<SwapOutlined />}
              disabled={!data.selected}
              onClick={handleOpenSpareModal}
            >
              {lang.handle("Repuesto")}
            </Button>
          )}

          <SpareModal
            navigate={navigate}
            showModal={ui.showModal}
            closeSpareModal={handleCloseSpareModal}
            lang={lang}
            id_stock={selectedId}
          />
        </Space>
      </div>
    </div>
  );
}

export default withLocale(withNavigation(KiboxManufactureList));
